import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import apiClient from "../../../services/apiClient";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import ReportTable from "../../common/ReportTable/ReportTable";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import { guidOrNull } from "../../../utils/guidUtils";
import {
  resolveSessionStaffId,
  userHasSessionPermission,
} from "../../../utils/permissionUtils";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import ConfirmModal from "../../common/ConfirmModal";
import InfoAlert from "../../common/InfoAlert";
import useConfirmModal from "../../../hooks/useConfirmModal";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { getPermissionMessage, ACTIONS } from "../../../constants/message";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { extractApiTableRows } from "./isfSiteSetupService";
import {
  ISF_REPORT_ACTIONS_COLUMN,
  ISF_REPORT_PINNED_COLUMNS,
  IsfReportRowActions,
  matchTextColumnSearch,
} from "./isfReportTable";
import "./isfReportTable.scss";

const REMOVED_DOCS_FIELD_OPTIONS = [
  { key: "StudyName", label: "Study" },
  { key: "TemplateCategory", label: "Category" },
  { key: "Name", label: "Name" },
  { key: "CreatedBy", label: "Owner" },
  { key: "CreatedDate", label: "Date & Time" },
  { key: ISF_REPORT_ACTIONS_COLUMN, label: "Actions" },
];

const DEFAULT_REMOVED_DOCS_COLUMNS = [
  "StudyName",
  "TemplateCategory",
  "Name",
  "CreatedBy",
  "CreatedDate",
  ISF_REPORT_ACTIONS_COLUMN,
];

function normalizeRemovedItemId(value) {
  return String(value || "")
    .replace(/[{}]/g, "")
    .trim();
}

function resolveRemovedItemStudyId(row = {}) {
  return normalizeRemovedItemId(
    row.StudyId ||
      row.studyId ||
      row.StudyIdRef ||
      row.StudyRefId ||
      "",
  );
}

function resolveRemovedItemFolderId(row = {}) {
  return normalizeRemovedItemId(row.FolderId || row.folderId || "");
}

function resolveRemovedItemParentFolderId(row = {}) {
  return normalizeRemovedItemId(
    row.ParentFolderIdRef || row.parentFolderIdRef || "",
  );
}

function isRemovedDocumentRow(row = {}) {
  return String(row.NodeType || row.nodeType || "")
    .trim()
    .toLowerCase() === "document";
}

/** Document → original folder (backend un-archives folder path). Folder → original parent. */
function resolveRestorePayload(row = {}) {
  const folderId = resolveRemovedItemFolderId(row);
  const isDocument = isRemovedDocumentRow(row);

  if (isDocument) {
    const documentId = normalizeRemovedItemId(row.UniqueId || row.uniqueId);
    return {
      isDocument: true,
      actionType: "RestoreDocument",
      folderId,
      documentId,
      // Same folder it was removed from — not a new location.
      newFolderId: folderId,
    };
  }

  return {
    isDocument: false,
    actionType: "RestoreFolder",
    folderId,
    documentId: "",
    newFolderId: resolveRemovedItemParentFolderId(row) || null,
  };
}

const RemovedDocuments = () => {
  const { modalState, showConfirm, handleConfirm, handleCancel, hideConfirm } =
    useConfirmModal();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const [documents, setDocuments] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [removedReason, setRemovedReason] = useState("");
  const organizationId = userData?.OrganizationId;

  const showAlertRef = useRef(showAlert);

  useEffect(() => {
    showAlertRef.current = showAlert;
  });

  const getAllDocuments = useCallback(async () => {
    if (!organizationId) return;

    try {
      setListLoading(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ISF.GET_STUDY_FOLDERS,
        {
          OrganizationIdRef: guidOrNull(organizationId),
          ActionType: "GetAllDeletedDocAndFolders",
        },
      );

      const rows = extractApiTableRows(response?.data, "SuccessStory");
      if (Array.isArray(rows)) {
        setDocuments(rows);
        return;
      }

      if (response?.success !== 1 && response?.success !== true) {
        showAlertRef.current({
          title: "Error",
          message:
            response?.message ||
            "Could not load removed documents. Please try again.",
          type: "danger",
        });
      }
      setDocuments([]);
    } catch (error) {
      console.error("Error loading removed documents:", error);
      showAlertRef.current({
        title: "Error",
        message: error?.message || "Could not load removed documents.",
        type: "danger",
      });
      setDocuments([]);
    } finally {
      setListLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    getAllDocuments();
  }, [organizationId, getAllDocuments]);

  const handleRefresh = () => {
    getAllDocuments();
  };

  const executeRestore = useCallback(
    async (row, authRes) => {
      const studyId = resolveRemovedItemStudyId(row);
      const restore = resolveRestorePayload(row);
      const staffId = resolveSessionStaffId(userData);

      if (!staffId) {
        showAlert({
          title: "Error",
          message: "Could not resolve your staff account. Please sign in again.",
          type: "danger",
        });
        return;
      }

      if (!studyId || !restore.folderId) {
        showAlert({
          title: "Error",
          message: "Could not determine the original study folder for this item.",
          type: "danger",
        });
        return;
      }

      if (restore.isDocument && !restore.documentId) {
        showAlert({
          title: "Error",
          message: "Could not determine the document to restore.",
          type: "danger",
        });
        return;
      }

      try {
        showLoader("Restoring...");
        const response = await apiClient.post(
          API_ENDPOINTS.ISF.FILE_FOLDER_RESTORE,
          {
            ActionType: restore.actionType,
            FolderId: restore.folderId,
            EmailId: userData.EmailId,
            Password: authRes.Password,
            AccessCodeIndex0: authRes.AccessCodeIndex0,
            AccessCodeIndex1: authRes.AccessCodeIndex1,
            AccessCodeIndex2: authRes.AccessCodeIndex2,
            AccessCodeValue0: authRes.AccessCodeValue0,
            AccessCodeValue1: authRes.AccessCodeValue1,
            AccessCodeValue2: authRes.AccessCodeValue2,
            StudyRefId: studyId,
            Reason: authRes.Description,
            NewFolderId: restore.newFolderId,
            OrganizationIdRef: guidOrNull(userData?.OrganizationId),
            CreatedBy: staffId,
            DocumentId: restore.documentId,
          },
        );

        showAlert({
          title: response.success === 1 ? "Success" : "Error",
          message:
            response.message ||
            (response.success === 1
              ? "Item restored successfully."
              : "Restore failed."),
          type: response.success === 1 ? "success" : "danger",
        });

        if (response.success === 1) {
          await getAllDocuments();
        }
      } catch (error) {
        console.error("Error restoring item:", error);
        showAlert({
          title: "Error",
          message: error?.message || "Restore failed.",
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [userData, getAllDocuments],
  );

  const handleRestore = useCallback(
    (row) => {
      if (!userHasSessionPermission(userData, "RESTORE")) {
        showAlert({
          title: "Warning",
          message: getPermissionMessage(ACTIONS.RESTORE_FILE_FOLDER),
          type: "warning",
        });
        return;
      }

      const itemLabel = String(row?.Name || row?.FolderName || "this item").trim();
      const isDocument = isRemovedDocumentRow(row);

      showConfirm({
        title: "Confirm restore",
        message: isDocument
          ? `Restore document "${itemLabel}" to its original folder? If the folder was removed, it will be restored automatically.`
          : `Restore folder "${itemLabel}" to its original location?`,
        confirmText: "Yes, restore",
        confirmButtonClass: "btn-success",
        icon: "fas fa-undo text-success",
        onConfirm: async () => {
          hideConfirm();
          const authRes = await openAuthModal({
            showDescription: true,
            requireDescription: true,
          });
          if (authRes?.Success !== 1) return;

          const reason = String(authRes?.Description || "").trim();
          if (!reason) {
            showAlert({
              title: "Warning",
              message: "A reason is required to restore this item.",
              type: "warning",
            });
            return;
          }

          await executeRestore(row, authRes);
        },
        onCancel: hideConfirm,
      });
    },
    [userData, showAlert, showConfirm, hideConfirm, executeRestore],
  );

  const handleReason = useCallback(async (row) => {
    setRemovedReason(row.Reason);
    setShowReasonModal(true);
  }, []);

  const tableActions = useMemo(
    () => [
      { label: "Reason", onClick: handleReason },
      { label: "Restore", onClick: handleRestore },
    ],
    [handleReason, handleRestore],
  );

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === ISF_REPORT_ACTIONS_COLUMN) {
        return <IsfReportRowActions row={row} actions={tableActions} />;
      }
      if (field === "CreatedDate") {
        return utils.formatDateTimeDisplay(row.CreatedDate);
      }
      return String(row[field] ?? "—");
    },
    [tableActions],
  );

  const matchColumnSearch = useCallback(
    (row, field, searchValue) =>
      matchTextColumnSearch(row, field, searchValue, ["CreatedDate"]),
    [],
  );

  const getFieldLabel = useCallback(
    (field) =>
      REMOVED_DOCS_FIELD_OPTIONS.find((option) => option.key === field)?.label ||
      field,
    [],
  );

  const layoutStorageKey = userData?.OrganizationId
    ? `isf-removed-docs-report-${userData.OrganizationId}`
    : "";

  return (
    <div className="isf-report-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Removed Documents" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: "Refresh",
            onClick: handleRefresh,
          },
        ]}
      />

      <div className="isf-report-table-wrap">
        <ReportTable
          rows={documents}
          fieldOptions={REMOVED_DOCS_FIELD_OPTIONS}
          defaultColumns={DEFAULT_REMOVED_DOCS_COLUMNS}
          getFieldLabel={getFieldLabel}
          renderCell={renderReportCell}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          pinnedRightColumns={ISF_REPORT_PINNED_COLUMNS}
          pinnedRightColumnWidthRem={4.5}
          hideAddColumnFields={[ISF_REPORT_ACTIONS_COLUMN]}
          hideColumnHeaderActionsFields={[ISF_REPORT_ACTIONS_COLUMN]}
          persistLayoutKey={layoutStorageKey}
          loading={listLoading}
        />
      </div>

      {showReasonModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Removed reason</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowReasonModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">{removedReason || ""}</div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowReasonModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={modalState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        {...modalState}
      />

      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

export default RemovedDocuments;
